import { useEffect, useRef } from 'react'
import { useBrowserObject } from './useBrowserObject'

const parseEvent = (event) => event.split('.')

const getWrappedHandler = (event, handler) => {
  const [ev, modifier] = parseEvent(event)

  let newHandler = (domEvent) => {
    handler(domEvent)
  }

  if (ev.startsWith('key') && modifier) {
    newHandler = (domEvent) => {
      if (domEvent.key === modifier) {
        handler(domEvent)
      }
    }
  }

  return [ev, newHandler]
}

export const useEventHandler = (event, callback, target) => {
  const handlerRef = useRef()
  const eventRef = useRef()

  target = target || useBrowserObject('window')

  useEffect(() => {
    const [parsedEvent, parsedHandler] = getWrappedHandler(event, callback)

    eventRef.current = parsedEvent
    handlerRef.current = parsedHandler
  }, [event, callback])

  useEffect(() => {
    target.addEventListener(eventRef.current, handlerRef.current)

    return () => target.removeEventListener(eventRef.current, handlerRef.current)
  }, [])
}

// event can be any dom event, key events can be handled with modifiers - ex: keyup.Escape
export const useConditionalEventHandler = (condition, event, callback, target) => {
  const handlerRef = useRef()
  const eventRef = useRef()

  target = target || useBrowserObject('window')

  useEffect(() => {
    const [parsedEvent, parsedHandler] = getWrappedHandler(event, callback)

    eventRef.current = parsedEvent
    handlerRef.current = parsedHandler
  }, [event, callback])

  useEffect(() => {
    if (condition) {
      target.addEventListener(eventRef.current, handlerRef.current)
    } else {
      target.removeEventListener(eventRef.current, handlerRef.current)
    }

    return () => target.removeEventListener(eventRef.current, handlerRef.current)
  }, [condition])
}
